import { useEffect, useMemo, useState } from "react";
import { api, formatTRY, formatNum } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { FinanceDetailDialog } from "@/components/FinanceDetailDialog";
import { DateRangeFilter, PRESETS } from "@/components/DateRangeFilter";
import {
  TrendingUp, TrendingDown, Wallet, ShoppingBag, AlertTriangle, ChefHat, Apple, PiggyBank, HeartHandshake, Leaf,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, accent, testid, sub, onClick }) => (
  <div
    data-testid={testid}
    onClick={onClick}
    role={onClick ? "button" : undefined}
    className={`animate-fade-up rounded-xl border border-border bg-card p-5 transition-all duration-200 hover:border-primary/50 ${onClick ? "cursor-pointer hover:-translate-y-0.5" : ""}`}
  >
    <div className="flex items-center justify-between">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${accent}`}>
        <Icon className="h-4 w-4" />
      </div>
    </div>
    <p className="mt-3 font-heading text-2xl font-extrabold tracking-tight">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
    {onClick && <p className="mt-2 text-[10px] font-medium uppercase tracking-wider text-primary/80">Detay için tıklayın</p>}
  </div>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [detail, setDetail] = useState(null);
  const [range, setRange] = useState(() => { try { return JSON.parse(sessionStorage.getItem("fh_dash_range")) || PRESETS.all(); } catch { return PRESETS.all(); } });
  const { user } = useAuth();

  const params = useMemo(() => { const p = {}; if (range.start) p.start = range.start; if (range.end) p.end = range.end; return p; }, [range]);
  const reload = () => api.get("/dashboard", { params }).then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { sessionStorage.setItem("fh_dash_range", JSON.stringify(range)); reload(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDetail = (kind, title, extra = {}) => setDetail({ kind, title, start: range.start, end: range.end, ...extra });
  const monthRange = (ym) => { const [y, m] = ym.split("-").map(Number); const last = new Date(y, m, 0).getDate(); return { start: `${ym}-01`, end: `${ym}-${String(last).padStart(2, "0")}` }; };
  const onBar = (kind) => (entry) => { if (entry?.month) openDetail(kind, `${entry.month} — ${kind === "income" ? "Gelir" : "Gider"} Kalemleri`, monthRange(entry.month)); };

  if (!d) return <div className="text-muted-foreground">Yükleniyor…</div>;

  const pieColors = ["var(--chart-1)", "var(--chart-2)", "var(--chart-3)", "var(--chart-4)", "var(--chart-5)"];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight">Özet & Genel Bakış</h1>
        <p className="mt-1 text-sm text-muted-foreground" data-testid="dashboard-scope">{user?.role === "admin" ? "Tüm kullanıcıların verileri — işletmenizin genel finansal ve operasyonel durumu" : "Size ait verilerle finansal ve operasyonel durumunuz"}</p>
      </div>

      <DateRangeFilter range={range} onChange={setRange} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-income" icon={Wallet} label="Toplam Gelir" value={formatTRY(d.total_income)}
          accent="bg-primary/15 text-primary" sub={`Kasa: ${formatTRY(d.sales_revenue)}${d.retail?.revenue > 0 ? ` · Perakende: ${formatTRY(d.retail.revenue)} (kâr ${formatTRY(d.retail.profit)})` : ""}`}
          onClick={() => openDetail("income", "Toplam Gelir Kalemleri")} />
        <StatCard testid="stat-expense" icon={TrendingDown} label="Toplam Gider" value={formatTRY(d.expenses + d.cogs)}
          accent="bg-destructive/15 text-destructive" sub={`Gider: ${formatTRY(d.expenses)} · Ürün maliyeti: ${formatTRY(d.cogs)}${d.purchases > 0 ? ` · Stok alımı ${formatTRY(d.purchases)} (ayrı)` : ""}`}
          onClick={() => openDetail("expense", "Toplam Gider Kalemleri")} />
        <StatCard testid="stat-profit" icon={d.net_profit >= 0 ? TrendingUp : TrendingDown}
          label="Net Kâr / Zarar" value={formatTRY(d.net_profit)}
          accent={d.net_profit >= 0 ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}
          sub={`${d.net_profit >= 0 ? "Kârdasınız 🎉" : "Zarar"} · Kazanılan VP: ${formatNum(d.vp_earned || 0, 1)}`} />
        <StatCard testid="stat-sales" icon={ShoppingBag} label="Toplam Satış" value={formatNum(d.total_sales_count, 0)}
          accent="bg-accent/15 text-accent" sub={d.open_tabs?.count > 0 ? `${d.recipes_count} ürün · Açık hesap: ${d.open_tabs.count} · ${formatTRY(d.open_tabs.total)}` : `${d.recipes_count} ürün çeşidi`} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-5 lg:col-span-2">
          <h3 className="font-heading text-lg font-semibold">Aylık Gelir / Gider</h3>
          <p className="text-xs text-muted-foreground">Bir ayın çubuğuna tıklayarak o ayın kalemlerini görün</p>
          <div className="mt-4 h-72" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis dataKey="month" stroke="var(--muted-foreground)" fontSize={12} />
                <YAxis stroke="var(--muted-foreground)" fontSize={12} />
                <Tooltip
                  contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--foreground)" }}
                  formatter={(v) => formatTRY(v)}
                />
                <Bar dataKey="income" name="Gelir" fill="var(--chart-1)" radius={[6, 6, 0, 0]} cursor="pointer" onClick={onBar("income")} />
                <Bar dataKey="expense" name="Gider" fill="var(--chart-3)" radius={[6, 6, 0, 0]} cursor="pointer" onClick={onBar("expense")} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading text-lg font-semibold">Gider Dağılımı</h3>
          {d.expense_by_category.length === 0 ? (
            <p className="mt-10 text-center text-sm text-muted-foreground">Henüz gider kaydı yok</p>
          ) : (
            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={d.expense_by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                    {d.expense_by_category.map((e, i) => (
                      <Cell key={e.category} fill={pieColors[i % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: 12, color: "var(--foreground)" }}
                    formatter={(v) => formatTRY(v)}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Kritik Stok Uyarıları</h3>
            {d.low_stock_count > 0 && (
              <span className="flex items-center gap-1 rounded-full bg-destructive/15 px-2.5 py-1 text-xs font-semibold text-destructive">
                <AlertTriangle className="h-3 w-3" /> {d.low_stock_count}
              </span>
            )}
          </div>
          {d.low_stock.length === 0 ? (
            <p className="mt-6 text-sm text-muted-foreground">Tüm stoklar güvenli seviyede ✅</p>
          ) : (
            <div className="mt-4 space-y-2" data-testid="dashboard-low-stock">
              {d.low_stock.map((i) => (
                <div key={i.id} className="flex items-center justify-between rounded-lg bg-destructive/10 px-3 py-2">
                  <span className="text-sm font-medium">{i.name}</span>
                  <span className={`font-mono text-xs ${i.current_stock < 0 ? "font-bold text-destructive" : "text-destructive"}`}>
                    {i.current_stock < 0 ? "−" : ""}{formatNum(Math.abs(i.current_stock), 0)} / {formatNum(i.min_stock_alert, 0)} {i.unit}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading text-lg font-semibold">En Çok Satan Ürünler</h3>
          {d.top_products.length === 0 ? (
            <p className="mt-6 text-sm text-muted-foreground">Henüz satış yok</p>
          ) : (
            <div className="mt-4 space-y-3">
              {d.top_products.map((p, i) => (
                <div key={p.name} className="flex items-center gap-3">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/15 text-xs font-bold text-primary">
                    {i + 1}
                  </span>
                  <span className="flex-1 text-sm font-medium">{p.name}</span>
                  <span className="font-mono text-sm text-muted-foreground">{p.qty} adet</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
          <Apple className="h-5 w-5 text-primary" />
          <div><p className="text-xs text-muted-foreground">Malzeme</p><p className="font-heading font-bold">{d.ingredients_count}</p></div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
          <ChefHat className="h-5 w-5 text-accent" />
          <div><p className="text-xs text-muted-foreground">Ürün / Reçete</p><p className="font-heading font-bold">{d.recipes_count}</p></div>
        </div>
        <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
          <PiggyBank className="h-5 w-5 text-primary" />
          <div><p className="text-xs text-muted-foreground">Diğer Gelir</p><p className="font-heading font-bold">{formatTRY(d.other_income)}</p></div>
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold">Kasa, Kulüp ve Genel Toplam</h3>
        <p className="text-xs text-muted-foreground">Kasa, Kulüp ve Herbalife ayrı tutulur; Genel Toplam üçünün birleşimidir. Kartlara tıklayarak kalemleri görün.</p>
        <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard testid="stat-cafe-net" icon={ShoppingBag} label="Kasa Net (kulüp hariç)" value={formatTRY(d.net_profit)}
            accent={d.net_profit >= 0 ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}
            sub={`Gelir ${formatTRY(d.total_income)} · Gider ${formatTRY(d.expenses + d.cogs + (d.retail?.cost || 0) + (d.retail?.vat || 0))}`}
            onClick={() => openDetail("cafe", "Kasa — Gelir / Gider Kalemleri (kulüp hariç)")} />
          <StatCard testid="stat-club-net" icon={HeartHandshake} label="Beslenme Kulübü Net Kâr" value={formatTRY(d.club_profit ?? d.club_net ?? 0)}
            accent={(d.club_profit ?? 0) >= 0 ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}
            sub={`Tahsilat ${formatTRY(d.club_income || 0)} · Sahip payı ${formatTRY(d.club_commission || 0)} · Öğün maliyeti ${formatTRY(d.club_meal_cost || 0)} (${d.club_meals || 0} öğün)`}
            onClick={() => openDetail("club", "Beslenme Kulübü — Tahsilat, Sahip Payı ve Öğün Maliyeti")} />
          <StatCard testid="stat-herbalife-net" icon={Leaf} label="Herbalife Net Kazanç" value={formatTRY(d.herbalife_net || 0)}
            accent="bg-primary/15 text-primary" sub="MyHerbalife ekstrelerinden (vergi sonrası net)"
            onClick={() => openDetail("herbalife", "MyHerbalife — Aylık Net Kazançlar")} />
          <StatCard testid="stat-grand-net" icon={d.grand_net >= 0 ? TrendingUp : TrendingDown} label="Genel Toplam Kâr / Zarar" value={formatTRY(d.grand_net || 0)}
            accent={d.grand_net >= 0 ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"}
            sub={`Toplam gelir ${formatTRY(d.grand_income || 0)} · Toplam gider ${formatTRY(d.grand_expense || 0)}`}
            onClick={() => openDetail("all", "Genel Toplam — Tüm Gelir ve Gider Kalemleri")} />
        </div>
      </div>
      <FinanceDetailDialog detail={detail} onClose={() => setDetail(null)} onChanged={reload} />
    </div>
  );
}
